import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';

import { App, NavigationSelectButton } from '@ingrid/luterkort-react';
import { useLogoMapContext } from '@ingrid/react-shipping-logo';

import { ConditionalWrapper } from '~src/components/ConditionalWrapper/ConditionalWrapper';
import { MerchantSiteDialog } from '~src/components/MerchantSiteDialog/MerchantSiteDialog';
import { useDetailedMerchantListQuery } from '~src/pages/merchant/queries';

import { useSite } from '../hooks/useSite';
import { useTracking } from '../hooks/useTracking';
import { AppRoute } from '../routes';

function normalizeName(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .split(' ')[0];
}

function MerchantLogo() {
  const { logoMap: logos = [], host } = useLogoMapContext();
  const { merchants } = useDetailedMerchantListQuery();
  const { merchant, site } = useSite();

  const currentMerchantName = merchant?.name ?? merchants[0].name ?? '';
  const currentSiteName = site?.name ?? merchants[0].sites[0].name ?? '';

  const logoEntry = logos.find(({ name, patterns }) => {
    return (
      // Find by normalized name
      normalizeName(name) === normalizeName(currentMerchantName) ||
      // Find by merchant name pattern
      patterns.includes(currentMerchantName) ||
      // Find by site name pattern
      patterns.includes(currentSiteName)
    );
  });

  const logo = `${host}${logoEntry?.pathRound30 ?? '/static/shipping-logos/logo-ingrid-round-30.svg'}`;

  return <img alt={merchant?.name ?? 'Ingrid'} src={logo} width="30" />;
}

function MerchantName() {
  const { merchants } = useDetailedMerchantListQuery();
  const { site } = useSite();

  return <App.LogoTitle>{site?.name ?? merchants[0].sites[0].name}</App.LogoTitle>;
}

export function AppMerchant() {
  const { merchants } = useDetailedMerchantListQuery();
  const { site } = useSite();

  const [isOpen, setIsOpen] = useState<boolean>(!site?.id);

  const tracking = useTracking();
  const navigate = useNavigate();

  const hasMultipleMerchantsOrSites = merchants.length > 1 || merchants[0].sites.length > 1;

  if (site?.id) {
    tracking.group(site.id);
  }

  return (
    <Fragment>
      <ConditionalWrapper
        wrapper={c => <NavigationSelectButton onClick={() => setIsOpen(true)}>{c}</NavigationSelectButton>}
        when={hasMultipleMerchantsOrSites}
      >
        <MerchantLogo />
        <MerchantName />
      </ConditionalWrapper>

      <MerchantSiteDialog
        defaultValue={{
          merchantId: merchants.find(m => m.sites.some(s => s.id === site?.id))?.id ?? merchants[0].id,
          siteId: site?.id ?? merchants[0].sites[0].id,
        }}
        onSubmit={({ siteId }) => navigate(`/${AppRoute.SITE}/${siteId}`)}
        onClose={() => setIsOpen(false)}
        cancelable={!!site?.id}
        isOpen={isOpen}
      />
    </Fragment>
  );
}
